import React, { useContext, useMemo } from "react";
import clsx from "clsx";
import { Col, Row, Form } from "react-bootstrap";

import { Button, Skeleton, RequiredMark } from "components";
import {
	ApplicationEntryProgressContext,
	BrandingColorContext,
	RequiredMarkContext,
} from "components/contexts";
import { generateAutotestValue } from "helpers";

import Option from "./dropDownOptions/option";
import OptionGroup from "./dropDownOptions/optionGroup";

import styles from "./formField.module.scss";

const ONLY_BORDER_ERROR = "onlyBorderError";

export const CustomDropdownByField = ({
	field,
	label,
	items = [],
	disabled,
	readOnly,
	input,
	type,
	smLabel = 6,
	labelStyle = {},
	smSelect = 6,
	selectStyle = {},
	meta: { touched, error, warning },
	tabIndex,
	loading,
	useFirstAsDefault,
	emptyValue = "",
	pleaseSelectText = "Please Select",
	hideRequiredMark, //works only with RequiredMarkContext
	requiredMark = false,
	wrapperClassName,
	optgroup = false,
	autoTest = "",
	labelName = "name",
	isWhiteLabel = false,
	customBrandingColorRule = input => input.value,
	onMouseEnter,
	onMouseLeave,
	icon,
	errorIcon = undefined,
	warningIcon = undefined,
	...props
}) => {
	const showRequiredMark =
		(useContext(RequiredMarkContext) && !hideRequiredMark && !disabled) ||
		requiredMark;

	const brandingColor =
		useContext(BrandingColorContext) || props.brandingColor;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);

	const handleBlur = () => {
		input.onBlur(input.value);
		triggerSave?.();
	};

	const showLoader =
		!disabled && (loading || (loading === undefined && !items.length));
	const disabledClass = disabled && !isWhiteLabel ? styles.disabled : "";

	const showError = touched && !disabled && !showLoader;

	const warningMessage = useMemo(
		() =>
			warning && (
				<p className={styles.spanWarn}>
					{warningIcon}
					{warning}
				</p>
			),
		[warning, warningIcon],
	);

	const errorMessage = useMemo(
		() =>
			error && (
				<p className={styles.spanError}>
					{errorIcon}
					{error}
				</p>
			),
		[error, errorIcon],
	);

	const errorText =
		(error !== ONLY_BORDER_ERROR && showError && touched && errorMessage) ||
		warningMessage;

	const validationClass =
		showError && ((error && styles.error) || (warning && styles.warning));

	const labelElement = label && (
		<Form.Label
			column
			sm={smLabel}
			className={`${disabledClass} ${styles.formGroup}`}
			// style={{ flex: "0 0 21%", maxWidth: "21%" }}
			style={labelStyle}
		>
			{label}
			{showRequiredMark && <RequiredMark />}
		</Form.Label>
	);

	const handleOnClick = e => {
		if (disabled) {
			return;
		}
		if (typeof props.onIconClick === "function") {
			props.onIconClick();
		}
	};

	return (
		<Form.Group
			className={clsx(styles.dropdownBlockValid, wrapperClassName)}
			as={Row}
		>
			{labelElement}
			<Col sm={smSelect} style={selectStyle}>
				<Skeleton height={34} display={showLoader ? "" : "None"} />
				<div className="d-flex">
					<select
						{...input}
						name={input.name}
						type={type}
						value={input.value}
						onChange={input.onChange}
						onBlur={handleBlur}
						className={clsx(
							validationClass,
							styles.dropdown,
							customBrandingColorRule(input) &&
								brandingColor &&
								styles.brandingColor,
						)}
						tabIndex={tabIndex}
						disabled={disabled}
						readOnly={readOnly}
						style={{ display: showLoader ? "None" : "" }}
						data-autotest={`select${generateAutotestValue(
							autoTest,
							input.name,
						)}`}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
					>
						{!useFirstAsDefault && (
							<option
								key={`${input.name}default`}
								value={emptyValue}
								className={styles.dropdownItemValid}
							>
								{pleaseSelectText}
							</option>
						)}
						{optgroup ? (
							<OptionGroup
								disabled={disabled}
								items={items}
								field={field}
							/>
						) : (
							<Option
								disabled={disabled}
								items={items}
								field={field}
								labelName={labelName}
							/>
						)}
					</select>
					{icon && (
						<Button
							onClick={handleOnClick}
							icon={icon}
							btnIcon
							variant="secondary"
							title=""
							disabled={disabled}
						/>
					)}
				</div>

				{errorText}
			</Col>
		</Form.Group>
	);
};

export const CustomDropdownById = props =>
	CustomDropdownByField({ ...props, field: "id" });

export const CustomDropdownByValue = props => {
	return CustomDropdownByField({ field: "value", ...props });
};

export const CustomDropdownByName = props =>
	CustomDropdownByField({ field: "name", ...props });

export const CustomDropdownByDisplayName = props =>
	CustomDropdownByField({ field: "displayName", ...props });

export const CustomHouseAccountDropdownByValue = props =>
	CustomDropdownByField({
		field: "value",
		pleaseSelectText: "House Account",
		...props,
	});
